<template>
  <div :class="['clock-question-input', { 'is-disabled': disabled }]">
    <p class="clock-hint">先选择上午或下午，再拖动橙色时针和蓝色分针。也可以聚焦指针后使用方向键微调。</p>
    <section
      v-for="(group, groupIndex) in displayValue"
      :key="`${group.type || 'group'}-${groupIndex}`"
      class="clock-group"
    >
      <div class="clock-list">
        <article
          v-for="(time, timeIndex) in group.data"
          :key="timeIndex"
          class="clock-card"
        >
          <div class="clock-card__header">
            <strong>第 {{ timeIndex + 1 }} 个时间</strong>
            <button type="button" class="clock-reset" :disabled="disabled" @click="resetTime(groupIndex, timeIndex)">重置</button>
          </div>

          <div class="period-switch" role="group" aria-label="选择上午或下午">
            <button
              v-for="period in periods"
              :key="period"
              type="button"
              :class="{ 'is-active': time.period === period }"
              :aria-pressed="time.period === period ? 'true' : 'false'"
              :disabled="disabled"
              @click="setPeriod(groupIndex, timeIndex, period)"
            >{{ period }}</button>
          </div>

          <svg
            class="clock-face"
            viewBox="0 0 240 240"
            role="group"
            :aria-label="`模拟钟表，当前时间 ${timeText(time)}`"
            @pointermove="moveHand"
            @pointerup="finishHandDrag"
            @pointercancel="finishHandDrag"
          >
            <circle class="clock-face__rim" cx="120" cy="120" r="108" />
            <circle class="clock-face__inner" cx="120" cy="120" r="99" />

            <line
              v-for="tick in tickMarks"
              :key="`tick-${tick.index}`"
              :x1="tick.x1"
              :y1="tick.y1"
              :x2="tick.x2"
              :y2="tick.y2"
              :class="['clock-tick', { 'is-hour': tick.index % 5 === 0 }]"
            />

            <text
              v-for="number in hourNumbers"
              :key="`hour-number-${number.value}`"
              :x="number.x"
              :y="number.y"
              class="clock-number"
            >{{ number.value }}</text>

            <g
              class="clock-hand-control clock-hand-control--hour"
              role="slider"
              :tabindex="disabled ? -1 : 0"
              aria-label="时针"
              aria-valuemin="1"
              aria-valuemax="12"
              :aria-valuenow="hasValue(time.hour) ? displayHour(time) : null"
              :aria-valuetext="hasValue(time.hour) ? `${displayHour(time)} 时` : '未设置'"
              @pointerdown.stop.prevent="startHandDrag(groupIndex, timeIndex, 'hour', $event)"
              @keydown="adjustHandFromKeyboard(groupIndex, timeIndex, 'hour', $event)"
            >
              <line
                class="clock-hand-hit"
                x1="120"
                y1="120"
                :x2="handPoint(time, 'hour').x"
                :y2="handPoint(time, 'hour').y"
              />
              <line
                :class="['clock-hand clock-hand--hour', { 'is-empty': !hasValue(time.hour) }]"
                x1="120"
                y1="120"
                :x2="handPoint(time, 'hour').x"
                :y2="handPoint(time, 'hour').y"
              />
              <circle
                class="clock-hand-knob clock-hand-knob--hour"
                :cx="handPoint(time, 'hour').x"
                :cy="handPoint(time, 'hour').y"
                r="7"
              />
            </g>

            <g
              class="clock-hand-control clock-hand-control--minute"
              role="slider"
              :tabindex="disabled ? -1 : 0"
              aria-label="分针"
              aria-valuemin="0"
              aria-valuemax="59"
              :aria-valuenow="hasValue(time.minute) ? Number(time.minute) : null"
              :aria-valuetext="hasValue(time.minute) ? `${Number(time.minute)} 分` : '未设置'"
              @pointerdown.stop.prevent="startHandDrag(groupIndex, timeIndex, 'minute', $event)"
              @keydown="adjustHandFromKeyboard(groupIndex, timeIndex, 'minute', $event)"
            >
              <line
                class="clock-hand-hit"
                :x1="handPoint(time, 'minute', 64).x"
                :y1="handPoint(time, 'minute', 64).y"
                :x2="handPoint(time, 'minute').x"
                :y2="handPoint(time, 'minute').y"
              />
              <line
                :class="['clock-hand clock-hand--minute', { 'is-empty': !hasValue(time.minute) }]"
                x1="120"
                y1="120"
                :x2="handPoint(time, 'minute').x"
                :y2="handPoint(time, 'minute').y"
              />
              <circle
                class="clock-hand-knob clock-hand-knob--minute"
                :cx="handPoint(time, 'minute').x"
                :cy="handPoint(time, 'minute').y"
                r="7"
              />
            </g>

            <circle class="clock-pin" cx="120" cy="120" r="6" />
          </svg>

          <div class="clock-readout" aria-live="polite">
            <span>{{ time.period || '请选择时段' }}</span>
            <strong>{{ timeText(time, false) }}</strong>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
const CLOCK_CENTER = 120

export default {
  name: 'ClockQuestionInput',
  props: {
    groups: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      periods: ['上午', '下午'],
      activeDrag: null
    }
  },
  computed: {
    displayValue() {
      return Array.isArray(this.value) && this.value.length ? this.value : this.valueFromGroups()
    },
    tickMarks() {
      return Array.from({ length: 60 }, (_, index) => {
        const angle = index * 6
        const outer = this.pointForAngle(angle, 95)
        const inner = this.pointForAngle(angle, index % 5 === 0 ? 84 : 90)
        return { index, x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y }
      })
    },
    hourNumbers() {
      return Array.from({ length: 12 }, (_, index) => {
        const value = index + 1
        const point = this.pointForAngle(value * 30, 70)
        return { value, x: point.x, y: point.y + 5 }
      })
    }
  },
  watch: {
    groups: {
      deep: true,
      immediate: true,
      handler() {
        if ((!Array.isArray(this.value) || !this.value.length) && this.groups.length) {
          this.$emit('input', this.valueFromGroups())
        }
      }
    }
  },
  beforeDestroy() {
    this.releaseActivePointer()
  },
  methods: {
    valueFromGroups() {
      return (Array.isArray(this.groups) ? this.groups : []).map((group, index) => ({
        type: String(group && group.type !== undefined ? group.type : index),
        data: Array.from({ length: Math.max(1, Number(group && group.time_count) || 1) }, () => ({ period: '', hour: '', minute: '' }))
      }))
    },
    hasValue(value) {
      return value !== null && typeof value !== 'undefined' && String(value) !== ''
    },
    pointForAngle(angle, length) {
      const radians = angle * Math.PI / 180
      return {
        x: CLOCK_CENTER + Math.sin(radians) * length,
        y: CLOCK_CENTER - Math.cos(radians) * length
      }
    },
    displayHour(time) {
      if (!time || !this.hasValue(time.hour)) return 12
      const hour = Number(time.hour)
      const display = hour % 12
      return display === 0 ? 12 : display
    },
    handPoint(time, hand, length = null) {
      const minute = time && this.hasValue(time.minute) ? Number(time.minute) : 0
      if (hand === 'minute') return this.pointForAngle(minute * 6, length === null ? 82 : length)
      const angle = (this.displayHour(time) % 12) * 30
      return this.pointForAngle(angle, length === null ? 57 : length)
    },
    timeText(time, includePeriod = true) {
      const period = time && time.period ? `${time.period} ` : ''
      const hour = time && this.hasValue(time.hour) ? String(this.displayHour(time)).padStart(2, '0') : '--'
      const minute = time && this.hasValue(time.minute) ? String(time.minute).padStart(2, '0') : '--'
      return `${includePeriod ? period : ''}${hour}:${minute}`
    },
    hourTo24(displayHour, period) {
      const hour = Number(displayHour)
      if (period === '下午') return hour === 12 ? 12 : hour + 12
      return hour === 12 ? 0 : hour
    },
    cloneValue() {
      return this.displayValue.map(group => ({
        ...group,
        data: Array.isArray(group.data) ? group.data.map(time => ({ ...time })) : []
      }))
    },
    updateTime(groupIndex, timeIndex, patch) {
      if (this.disabled) return
      const next = this.cloneValue()
      if (!next[groupIndex] || !next[groupIndex].data[timeIndex]) return
      next[groupIndex].data[timeIndex] = {
        ...next[groupIndex].data[timeIndex],
        ...patch
      }
      this.$emit('input', next)
    },
    setPeriod(groupIndex, timeIndex, period) {
      const time = this.displayValue[groupIndex] && this.displayValue[groupIndex].data[timeIndex]
      if (!time) return
      const patch = { period }
      if (this.hasValue(time.hour)) {
        patch.hour = String(this.hourTo24(this.displayHour(time), period)).padStart(2, '0')
      }
      this.updateTime(groupIndex, timeIndex, patch)
    },
    resetTime(groupIndex, timeIndex) {
      this.updateTime(groupIndex, timeIndex, { period: '', hour: '', minute: '' })
    },
    startHandDrag(groupIndex, timeIndex, hand, event) {
      if (this.disabled) return
      const svg = event.currentTarget.ownerSVGElement
      if (!svg) return
      this.activeDrag = {
        groupIndex,
        timeIndex,
        hand,
        pointerId: event.pointerId,
        svg
      }
      if (svg.setPointerCapture) svg.setPointerCapture(event.pointerId)
      this.updateHandFromPointer(event)
    },
    moveHand(event) {
      if (!this.activeDrag || event.pointerId !== this.activeDrag.pointerId) return
      event.preventDefault()
      this.updateHandFromPointer(event)
    },
    finishHandDrag(event) {
      if (!this.activeDrag || event.pointerId !== this.activeDrag.pointerId) return
      this.releaseActivePointer()
    },
    releaseActivePointer() {
      if (!this.activeDrag) return
      const { svg, pointerId } = this.activeDrag
      if (svg && svg.releasePointerCapture && svg.hasPointerCapture && svg.hasPointerCapture(pointerId)) {
        svg.releasePointerCapture(pointerId)
      }
      this.activeDrag = null
    },
    updateHandFromPointer(event) {
      if (!this.activeDrag) return
      const { svg, groupIndex, timeIndex, hand } = this.activeDrag
      const rect = svg.getBoundingClientRect()
      const x = (event.clientX - rect.left) * 240 / rect.width - CLOCK_CENTER
      const y = (event.clientY - rect.top) * 240 / rect.height - CLOCK_CENTER
      const angle = (Math.atan2(x, -y) * 180 / Math.PI + 360) % 360
      const time = this.displayValue[groupIndex] && this.displayValue[groupIndex].data[timeIndex]
      if (!time) return

      if (hand === 'minute') {
        const minute = Math.round(angle / 6) % 60
        this.updateTime(groupIndex, timeIndex, { minute: String(minute).padStart(2, '0') })
      } else {
        const dialHour = Math.round(angle / 30) % 12 || 12
        const hour = this.hourTo24(dialHour, time.period)
        this.updateTime(groupIndex, timeIndex, { hour: String(hour).padStart(2, '0') })
      }
    },
    adjustHandFromKeyboard(groupIndex, timeIndex, hand, event) {
      if (this.disabled) return
      if (!['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp'].includes(event.key)) return
      event.preventDefault()
      const delta = ['ArrowRight', 'ArrowUp'].includes(event.key) ? 1 : -1
      const time = this.displayValue[groupIndex] && this.displayValue[groupIndex].data[timeIndex]
      if (!time) return

      if (hand === 'minute') {
        const current = this.hasValue(time.minute) ? Number(time.minute) : 0
        const minute = (current + delta + 60) % 60
        this.updateTime(groupIndex, timeIndex, { minute: String(minute).padStart(2, '0') })
      } else {
        const current = this.displayHour(time)
        const dialHour = ((current - 1 + delta + 12) % 12) + 1
        const hour = this.hourTo24(dialHour, time.period)
        this.updateTime(groupIndex, timeIndex, { hour: String(hour).padStart(2, '0') })
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/tokens';

.clock-question-input {
  display: grid;
  gap: 12px;
}

.clock-question-input.is-disabled {
  opacity: 0.65;
}

.clock-question-input.is-disabled .clock-face,
.clock-question-input.is-disabled .clock-hand-control {
  cursor: not-allowed;
  pointer-events: none;
}

.clock-hint {
  margin: 0;
  color: $color-text-secondary;
  font-size: 13px;
  line-height: 1.6;
}

.clock-group {
  display: grid;
  gap: 12px;
  padding: 14px;
  border: 1px solid $color-border-light;
  border-radius: var(--radius-md);
  background: $color-background;
}

.clock-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  gap: 14px;
}

.clock-card {
  display: grid;
  justify-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid $color-border;
  border-radius: var(--radius-lg);
  background: $color-surface;
  box-shadow: $shadow-xs;
}

.clock-card__header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  color: $color-text-primary;
}

.clock-reset {
  padding: 4px 9px;
  border: 0;
  border-radius: var(--radius-sm);
  background: $color-background;
  color: $color-text-secondary;
  cursor: pointer;
  font-size: 12px;
}

.period-switch {
  display: grid;
  width: min(220px, 100%);
  grid-template-columns: repeat(2, 1fr);
  padding: 3px;
  border-radius: var(--radius-lg);
  background: $color-background-dark;
}

.period-switch button {
  min-height: 34px;
  padding: 5px 12px;
  border: 0;
  border-radius: calc(var(--radius-lg) - 2px);
  background: transparent;
  color: $color-text-secondary;
  cursor: pointer;
  font-weight: 700;
}

.period-switch button.is-active {
  background: $color-surface;
  color: $color-primary;
  box-shadow: $shadow-xs;
}

.clock-face {
  display: block;
  width: min(260px, 100%);
  height: auto;
  overflow: visible;
  user-select: none;
}

.clock-face__rim {
  fill: $color-surface;
  stroke: #344054;
  stroke-width: 5;
}

.clock-face__inner {
  fill: #fffdf9;
  stroke: $color-border-light;
  stroke-width: 1;
}

.clock-tick {
  stroke: $color-text-tertiary;
  stroke-linecap: round;
  stroke-width: 1.5;
}

.clock-tick.is-hour {
  stroke: $color-text-primary;
  stroke-width: 3;
}

.clock-number {
  fill: $color-text-primary;
  font-size: 15px;
  font-weight: 800;
  text-anchor: middle;
}

.clock-hand-control {
  cursor: grab;
  outline: none;
  touch-action: none;
}

.clock-hand-control:active {
  cursor: grabbing;
}

.clock-hand-control:focus-visible .clock-hand-hit {
  stroke: rgba($color-info, 0.2);
}

.clock-hand-hit {
  stroke: transparent;
  stroke-linecap: round;
  stroke-width: 24;
}

.clock-hand {
  stroke-linecap: round;
  transition: opacity 0.15s ease;
}

.clock-hand--hour {
  stroke: $color-primary;
  stroke-width: 8;
}

.clock-hand--minute {
  stroke: $color-info;
  stroke-width: 5;
}

.clock-hand.is-empty {
  opacity: 0.3;
  stroke-dasharray: 6 5;
}

.clock-hand-knob {
  stroke: $color-surface;
  stroke-width: 3;
}

.clock-hand-knob--hour {
  fill: $color-primary;
}

.clock-hand-knob--minute {
  fill: $color-info;
}

.clock-pin {
  fill: #344054;
  stroke: $color-surface;
  stroke-width: 2;
  pointer-events: none;
}

.clock-readout {
  display: flex;
  min-width: 170px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: var(--radius-lg);
  background: $color-background;
  color: $color-text-secondary;
}

.clock-readout strong {
  color: $color-text-primary;
  font-size: 20px;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 480px) {
  .clock-group {
    padding: 10px;
  }

  .clock-list {
    grid-template-columns: minmax(0, 1fr);
  }

  .clock-card {
    padding: 12px 8px;
  }

  .clock-face {
    width: min(240px, 100%);
  }
}
</style>
